import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Collapse,
  HStack,
  Icon,
  Link,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { NavLink as RouterNavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useProfile } from '../auth/ProfileContext';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { planName } from '../data/plans';
import { WorkspaceSwitcher } from '../workspaces/WorkspaceSwitcher';
import {
  AdminIcon,
  ApprovalsIcon,
  BillingIcon,
  CommunityIcon,
  GalleriesIcon,
  LibraryIcon,
  OverviewIcon,
  ProfileIcon,
  SignOutIcon,
  StorageIcon,
  SystemIcon,
  UsageIcon,
  UsersIcon,
} from './NavIcons';

interface NavEntry {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
}

const creatorWorkspaceLinks: NavEntry[] = [
  { to: '/galleries', label: 'Client Galleries', icon: GalleriesIcon },
  { to: '/library', label: 'All photos', icon: LibraryIcon },
];

const personalLinks: NavEntry[] = [
  { to: '/my-galleries', label: 'Shared with me', icon: GalleriesIcon },
];

const accountLinks: NavEntry[] = [
  { to: '/profile', label: 'Profile', icon: ProfileIcon },
  { to: '/account', label: 'Plan & usage', icon: UsageIcon },
  { to: '/billing', label: 'Billing', icon: BillingIcon },
];

const adminLinks: NavEntry[] = [
  { to: '/admin', label: 'Overview', icon: OverviewIcon, end: true },
  { to: '/admin/photo-requests', label: 'Photo requests', icon: GalleriesIcon },
  { to: '/admin/users', label: 'Users', icon: UsersIcon },
  { to: '/admin/approvals', label: 'Approvals', icon: ApprovalsIcon },
  { to: '/admin/plans', label: 'Plans & storage', icon: StorageIcon },
  { to: '/admin/system', label: 'System', icon: SystemIcon },
  { to: '/admin/kitchen', label: 'iLand Kitchen', icon: OverviewIcon },
];

const COMMUNITY_OWNER = 'y.jeihouni@gmail.com';

// Every signed-in page sits in this frame: one side navigation, one content
// width, one background, so moving between galleries, profile, billing and
// admin never feels like switching sites.
const WorkspaceLayout: React.FC = () => {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const pageBg = useColorModeValue('#f6f6f4', '#111722');
  const color = useColorModeValue('#172033', 'gray.100');
  const border = useColorModeValue('gray.200', 'whiteAlpha.200');
  const muted = useColorModeValue('gray.500', 'gray.500');
  const idle = useColorModeValue('gray.700', 'gray.300');
  const strong = useColorModeValue('gray.900', 'white');
  const hoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.50');
  const activeBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.100');
  const guide = useColorModeValue('gray.200', 'whiteAlpha.200');

  const displayName = profile?.name || user?.name || user?.email || '';
  const isAdmin = profile?.isAdmin === true;
  const isClient = profile?.role === 'client';
  const workspaceLinks = isClient ? [] : creatorWorkspaceLinks;
  const visibleAccountLinks = isClient ? accountLinks.filter((entry) => entry.to === '/profile') : accountLinks;
  const inAdmin = pathname.startsWith('/admin');
  // Platform Admin folds away like a tree node; it opens by itself whenever
  // one of its pages is showing so the current page is never hidden.
  const [adminOpen, setAdminOpen] = useState(inAdmin);
  useEffect(() => {
    if (inAdmin) setAdminOpen(true);
  }, [inAdmin]);

  const handleSignOut = async () => {
    await signOut();
    if (!user?.isFederated) {
      navigate('/login', { replace: true });
    }
  };

  const item = (entry: NavEntry) => (
    <Link
      key={entry.to}
      as={RouterNavLink}
      to={entry.to}
      className={entry.to === '/my-galleries' && pathname.startsWith('/photos/') ? 'active' : undefined}
      end={entry.end}
      display="flex"
      alignItems="center"
      gap={3}
      px={3}
      py={1.5}
      borderRadius="md"
      fontSize="sm"
      fontWeight="500"
      color={idle}
      whiteSpace="nowrap"
      _hover={{ textDecoration: 'none', bg: hoverBg, color: strong }}
      sx={{
        '&.active': { bg: activeBg, color: 'orange.400', fontWeight: 600 },
      }}
    >
      <Icon as={entry.icon} boxSize={4} flexShrink={0} />
      {entry.label}
    </Link>
  );

  const section = (label: string, children: React.ReactNode) => (
    <Box mt={5} _first={{ mt: 0 }}>
      <Text px={3} mb={1.5} fontSize="xs" fontWeight="600" color={muted}>
        {label}
      </Text>
      <VStack align="stretch" spacing={0.5}>
        {children}
      </VStack>
    </Box>
  );

  const mobileLinks: NavEntry[] = [
    ...personalLinks,
    ...workspaceLinks,
    ...visibleAccountLinks,
    ...(isAdmin ? [{ to: '/admin', label: 'Platform Admin', icon: AdminIcon }] : []),
  ];

  return (
    <Box bg={pageBg} color={color} minH="80vh">
      <Box
        maxW="1360px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={{ base: 4, lg: 8 }}
        display="grid"
        gridTemplateColumns={{ base: '1fr', lg: '232px minmax(0, 1fr)' }}
        gap={{ base: 4, lg: 10 }}
        alignItems="start"
      >
        <Box gridColumn="1 / -1" maxW={{ base: '100%', lg: '232px' }}>
          <WorkspaceSwitcher />
        </Box>
        {/* Phones and tablets: one scrollable row of the same destinations. */}
        <HStack
          as="nav"
          aria-label="Workspace"
          display={{ base: 'flex', lg: 'none' }}
          spacing={1}
          overflowX="auto"
          mx={-4}
          px={4}
          pb={2}
          borderBottom="1px solid"
          borderColor={border}
        >
          {mobileLinks.map((entry) => (
            <Link
              key={entry.to}
              as={RouterNavLink}
              to={entry.to}
              className={entry.to === '/my-galleries' && pathname.startsWith('/photos/') ? 'active' : undefined}
              px={3}
              py={1.5}
              borderRadius="full"
              fontSize="sm"
              fontWeight="600"
              color={idle}
              whiteSpace="nowrap"
              _hover={{ textDecoration: 'none', bg: hoverBg }}
              sx={{ '&.active': { bg: 'orange.400', color: 'white' } }}
            >
              {entry.label}
            </Link>
          ))}
        </HStack>

        <Box
          as="aside"
          display={{ base: 'none', lg: 'flex' }}
          flexDirection="column"
          position="sticky"
          top="88px"
          maxH="calc(100vh - 110px)"
          overflowY="auto"
          pr={5}
          borderRight="1px solid"
          borderColor={border}
        >
          <HStack spacing={3} px={3} pb={5}>
            <Avatar size="sm" name={displayName} src={profile?.avatarUrl || undefined} bg="orange.400" color="white" />
            <Box minW={0}>
              <Text fontWeight="600" fontSize="sm" noOfLines={1}>
                {displayName}
              </Text>
              <Text fontSize="xs" color={muted}>
                {inAdmin ? 'Platform Admin' : `${planName(profile?.plan || 'free')} account plan`}
              </Text>
            </Box>
          </HStack>

          <Box as="nav" aria-label="Workspace">
            {section('Personal', personalLinks.map(item))}
            {workspaceLinks.length > 0 && section('Studio', workspaceLinks.map(item))}
            {section('Account', visibleAccountLinks.map(item))}
            {isAdmin &&
              section(
                'Platform Administration',
                <>
                  <Box
                    as="button"
                    type="button"
                    onClick={() => setAdminOpen((open) => !open)}
                    aria-expanded={adminOpen}
                    display="flex"
                    alignItems="center"
                    gap={3}
                    w="100%"
                    px={3}
                    py={2}
                    borderRadius="md"
                    fontSize="sm"
                    fontWeight="600"
                    color={inAdmin ? 'orange.400' : idle}
                    bg={inAdmin ? activeBg : 'transparent'}
                    _hover={{ bg: inAdmin ? activeBg : hoverBg, color: inAdmin ? 'orange.400' : strong }}
                  >
                    <Icon as={AdminIcon} boxSize={4} />
                    <Box as="span" flex="1" textAlign="left">
                      Platform Admin
                    </Box>
                    <ChevronDownIcon
                      boxSize={4}
                      transform={adminOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
                      transition="transform 150ms ease"
                    />
                  </Box>
                  {/* The settings pages hang off the group, like an indented tree. */}
                  <Collapse in={adminOpen} animateOpacity>
                    <VStack align="stretch" spacing={0.5} ml={5} mt={1} pl={2} borderLeft="1px solid" borderColor={guide}>
                      {adminLinks.filter(entry => !profile?.delegatedAdmin || ['/admin', '/admin/users', '/admin/approvals'].includes(entry.to)).map(item)}
                    </VStack>
                  </Collapse>
                </>
              )}
            {user?.email.toLowerCase() === COMMUNITY_OWNER &&
              section('Previews', item({ to: '/community', label: 'iLand Community', icon: CommunityIcon }))}
          </Box>

          <Box borderTop="1px solid" borderColor={border} mt={5} pt={3}>
            <Box
              as="button"
              type="button"
              onClick={handleSignOut}
              display="flex"
              alignItems="center"
              gap={3}
              w="100%"
              px={3}
              py={1.5}
              borderRadius="md"
              fontSize="sm"
              fontWeight="500"
              color={idle}
              _hover={{ bg: hoverBg, color: strong }}
            >
              <Icon as={SignOutIcon} boxSize={4} />
              Sign out
            </Box>
          </Box>
        </Box>

        <Box as="section" minW={0}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default WorkspaceLayout;
